// Site switcher — RON-5469. Prototype region: `#site-trigger` + `#dd-sites`.
//
// A custom dropdown rather than a `<select>`, for one concrete reason: the prototype puts the
// site's ADDRESS and a directions link inside the open panel, and a native select can only hold
// text. The first build used a `<select>` and had to spill the address across the top bar, which
// is what the screenshot comparison caught — it cluttered the bar and still had nowhere to put
// the link.
//
// `site_id` is required on every call (§2), so this is the board's identity, not a filter over it.

import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

// §4.2 of the invite contract: a location is "a pair or null — never half a coordinate", so both
// halves are checked rather than one being taken to imply the other.
export const hasLocation = (site) =>
  site?.location?.lat !== undefined &&
  site?.location?.lat !== null &&
  site?.location?.lng !== undefined &&
  site?.location?.lng !== null;

const ReceptionSitePicker = ({ sites, siteId, onSiteChange, counts }) => {
  const { t } = useTranslation("common");
  const [isOpen, setIsOpen] = useState(false);
  const [notice, setNotice] = useState(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;

    const onDocumentClick = (event) => {
      if (wrapRef.current?.contains(event.target)) return;

      setIsOpen(false);
      setNotice(null);
    };
    const onKeyDown = (event) => event.key === "Escape" && setIsOpen(false);

    document.addEventListener("mousedown", onDocumentClick);
    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.removeEventListener("mousedown", onDocumentClick);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen]);

  const site = sites.find((candidate) => candidate.id === siteId);

  return (
    <div className="reception-sitepick" ref={wrapRef}>
      <button
        type="button"
        className="reception-sitepick__trigger"
        onClick={() => {
          setNotice(null);
          setIsOpen((current) => !current);
        }}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={t("react_reception_site")}
      >
        <svg className="reception-sitepick__pin" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
          <circle cx="12" cy="10" r="3" />
        </svg>
        <span className="reception-sitepick__name">
          {site?.name || t("react_reception_site")}
        </span>
        <svg className="reception-sitepick__caret" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {isOpen && (
        <div className="reception-sitepick__menu" role="listbox">
          {/* Where this site is, and how to get to it — the half of the prototype's panel a
              receptionist actually uses when a visitor rings up lost. */}
          {site && (
            <div className="reception-sitepick__about">
              {/* The name as well as the address. The trigger above shows it too, but once the
                  panel is open the trigger is what you are trying to change — the card has to say
                  which site it is describing. */}
              {/* LOADED ONLY WHILE THE PANEL IS OPEN, and unmounted when it closes.

                  This screen sits at a desk all day. An iframe rendered with the page would hold a
                  connection to Google for the whole shift, on a screen that is behind a login and
                  may be on a network that blocks it — and a blocked iframe is a grey rectangle
                  where a map should be. Mounting it on open costs one request when somebody
                  actually wants to see where a site is.

                  `loading="lazy"` on top of that, so it is not fetched until it is in view.

                  The whole block is conditional on coordinates, which `GET /reception/sites` does
                  not return today — see the questions draft. It lights up on its own the day they
                  add `location`. */}
              {hasLocation(site) && (
                <iframe
                  className="reception-sitepick__map"
                  title={t("react_reception_site_map").replace("%s", site.name)}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  src={`https://www.google.com/maps?q=${site.location.lat},${site.location.lng}&z=15&output=embed`}
                />
              )}

              <p className="reception-sitepick__here">{site.name}</p>
              <p className="reception-sitepick__addr">{site.address}</p>
              {hasLocation(site) && (
                <a
                  className="reception-sitepick__dir"
                  href={`https://www.google.com/maps/dir/?api=1&destination=${site.location.lat},${site.location.lng}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t("react_reception_directions")} ↗
                </a>
              )}
            </div>
          )}

          {/* The prototype's `.dd-head`: the list's title on the left, the "All sites" link on the
              right. */}
          <div className="reception-sitepick__head">
            <span className="reception-sitepick__title">
              {t("react_reception_switch_location")}
            </span>
            {/* There is no all-sites board and there cannot be one: §2 makes `site_id` required
                because "a muster spanning three buildings would be a fire warden holding the wrong
                list". The link is kept to the prototype's design, and says so when pressed rather
                than doing nothing. */}
            <button
              type="button"
              className="reception-sitepick__all"
              onClick={() => setNotice(t("react_reception_all_sites_note"))}
            >
              {t("react_reception_all_sites")}
            </button>
          </div>

          {notice && <p className="reception-sitepick__notice">{notice}</p>}

          {sites.map((candidate) => {
            const isCurrent = candidate.id === siteId;

            return (
              <button
                key={candidate.id}
                type="button"
                role="option"
                aria-selected={isCurrent}
                className={`reception-sitepick__item${
                  isCurrent ? " reception-sitepick__item--current" : ""
                }`}
                onClick={() => {
                  setIsOpen(false);
                  if (!isCurrent) onSiteChange?.(candidate.id);
                }}
              >
                <span className="reception-sitepick__dot" aria-hidden="true" />

                <span className="reception-sitepick__text">
                  <span className="reception-sitepick__label">
                    {candidate.name}
                    {/* The prototype's subtitle is the site's location. It carries the address,
                        which is the field the API actually returns. */}
                    {candidate.address && (
                      <span className="reception-sitepick__sub"> · {candidate.address}</span>
                    )}
                  </span>

                  {/* The headcount. REAL for the site being shown — those are the board's own
                      counts, already on screen above. PLACEHOLDER for the others, because no
                      endpoint reports a site you are not looking at; see the note below. */}
                  <span className="reception-sitepick__meta">
                    {isCurrent && counts ? (
                      <>
                        <strong>{counts.onSite}</strong> {t("react_reception_on_site_lower")} ·{" "}
                        {counts.expected} {t("react_reception_expected_lower")}
                      </>
                    ) : (
                      <span className="reception-sitepick__placeholder">
                        {t("react_reception_counts_placeholder")}
                      </span>
                    )}
                  </span>
                </span>
                {/* The word, not a tick. A tick in a list of places reads as "selected"; on a
                    board that only ever shows one site, "current" is the truer claim. */}
                {isCurrent && (
                  <span className="reception-sitepick__current">
                    {t("react_reception_current")}
                  </span>
                )}
              </button>
            );
          })}

          {/* The prototype also shows a live headcount per site here ("47 on site · 12 expected")
              and an "All sites" mode. Neither is possible today: the board is ONE site by design
              (§2 — `site_id` is required, and a muster spanning three buildings would be a fire
              warden holding the wrong list), and there is no endpoint returning counts for a site
              you are not looking at.

              The backend raised exactly this as their §10 question 2 — whether reception should
              see other sites' counts — and said it is "a small addition and worth doing once
              rather than as a per-site fan-out from the browser". It is waiting on our answer,
              not on our code. */}
        </div>
      )}
    </div>
  );
};

export default ReceptionSitePicker;
